import React, { useEffect, useMemo, useState } from "react";
import { executeQuery } from "../../../graphql/client";

// Attribution tab (epic #293 phase 2): where new people come from and
// who brings them. Reads the hearAbout / refCode attributes phase 1
// stamps on socialUsers rows via the admin-only signupAttribution
// query. Three questions, top to bottom:
//   1. Which channel fills the funnel (sources, with how far each
//      channel's people get: started → account → approved → paid)
//   2. Is it trending (12-week signups, attributed share)
//   3. Who brings people (referral leaderboard, refCode resolved to
//      the member; unresolved codes are shown as-is so nothing hides)
// Captain scoreboard (#309): the "This month" / "Last month" windows
// are calendar months, captains on the roster get a ⭐, and the
// leader box writes the "most people brought" shout-out for David to
// paste into the chat or the content outbox. Phase 3 of #293 (what a
// referral earns) is still David's decision — this tab only counts.

const SOURCE_LABELS = {
  instagram: "Instagram",
  facebook: "Facebook",
  friend: "Friend / member",
  google: "Google",
  "walked-up": "Met at an event",
  other: "Other",
  "referral-link": "Referral link (no source picked)",
  unspecified: "Not asked / not answered",
};

const STATUS_LABELS = {
  lead: "lead",
  account: "account",
  approved: "approved",
  paid: "paid 💳",
};

// Window keys: "m:YYYY-MM" is a calendar month (server-side, in the
// group's time zone); a bare number is a rolling day count (0 = all).
const monthKey = (offset) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `m:${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const WINDOWS = [
  { key: monthKey(0), label: "This month" },
  { key: monthKey(-1), label: "Last month" },
  { key: "30", label: "30 days" },
  { key: "90", label: "90 days" },
  { key: "365", label: "1 year" },
  { key: "0", label: "All time" },
];
const windowVars = (key) =>
  key.startsWith("m:") ? { month: key.slice(2) } : { days: Number(key) };

const copyText = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fall through to the textarea path
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};

const referrerLabel = (r) =>
  r.resolved
    ? r.referrerName || r.referrerEmail || "Member"
    : `code ${r.refCode}`;

// The shout-out David pastes into the main chat (or the content
// outbox, #292) when the month closes. Ranking is the leaderboard's:
// paid first, then accounts, then total.
const buildShoutOut = (ranked, monthLabel, captainsOnly) => {
  const [lead, second] = ranked;
  if (!lead) return "";
  const who = (r) => referrerLabel(r);
  const n = (r) =>
    `${r.total} ${r.total === 1 ? "person" : "people"} joined through their link${
      r.paid ? ` (${r.paid} now paying)` : ""
    }`;
  const lines = [
    `🏆 Most people brought in ${monthLabel}${captainsOnly ? " (captains)" : ""}: ${who(lead)} — ${n(lead)}.`,
  ];
  if (second) lines.push(`Runner-up: ${who(second)} — ${n(second)}.`);
  lines.push(
    "Want your name here next month? Events tab → the green \"Bring a friend\" card → share your link. 🏐🌴"
  );
  return lines.join("\n");
};

const OCEAN = "var(--ab-ocean, #0e7c9b)";
const SEAFOAM = "var(--ab-seafoam, #e7f4f3)";

const sourceLabel = (s) => SOURCE_LABELS[s] || s;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const fmtWeek = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const pct = (n, d) => (d ? `${Math.round((100 * n) / d)}%` : "–");

const pill = (active) => ({
  border: active ? `2px solid ${OCEAN}` : "1px solid #d7dbe4",
  background: active ? SEAFOAM : "#fff",
  color: "#333",
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
});

const card = {
  border: "1px solid #e6e9f0",
  borderRadius: 10,
  background: "#fff",
  padding: "12px 14px",
  marginBottom: 14,
};

const muted = { color: "#6a7385", fontSize: 13 };

const th = {
  textAlign: "left",
  fontSize: 12,
  color: "#6a7385",
  fontWeight: 600,
  padding: "4px 6px",
  borderBottom: "1px solid #e6e9f0",
  whiteSpace: "nowrap",
};
const td = { padding: "6px", fontSize: 14, verticalAlign: "middle" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

// Thin single-hue bar: magnitude only, so one color. Width is relative
// to the largest value in the same table; label stays in text ink.
const Bar = ({ value, max }) => (
  <div
    style={{
      height: 8,
      borderRadius: 4,
      background: "#eef1f5",
      minWidth: 80,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        height: "100%",
        width: max ? `${Math.max(2, (100 * value) / max)}%` : 0,
        background: OCEAN,
        borderRadius: 4,
      }}
    />
  </div>
);

const Stat = ({ label, value, sub }) => (
  <div
    style={{
      ...card,
      marginBottom: 0,
      flex: "1 1 140px",
      padding: "10px 12px",
    }}
  >
    <div style={{ fontSize: 12, color: "#6a7385", fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "#1f2330" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#8a93a3" }}>{sub}</div>}
  </div>
);

const AttributionTab = () => {
  const [win, setWin] = useState(WINDOWS[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openRef, setOpenRef] = useState("");
  const [captainsOnly, setCaptainsOnly] = useState(false);
  const [copied, setCopied] = useState("");

  const load = async (windowKey) => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(
        `query SignupAttribution($days: Int, $month: String) { signupAttribution(days: $days, month: $month) }`,
        windowVars(windowKey)
      );
      setData(out?.signupAttribution ? JSON.parse(out.signupAttribution) : null);
    } catch (err) {
      setError(err.message || "Couldn't load attribution.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(win);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win]);

  const sources = data?.sources || [];
  const weeks = data?.weeks || [];
  const referrers = data?.referrers || [];
  const recent = data?.recent || [];
  const totals = data?.totals || {
    rows: 0,
    attributed: 0,
    referred: 0,
    referredByCaptains: 0,
    paidAttributed: 0,
  };
  const captainCount = referrers.filter((r) => r.isCaptain).length;
  const ranked = captainsOnly ? referrers.filter((r) => r.isCaptain) : referrers;
  const isMonth = win.startsWith("m:");
  const monthLabel = data?.month?.label || "";
  const shoutOut = isMonth ? buildShoutOut(ranked, monthLabel, captainsOnly) : "";

  const maxStarted = useMemo(
    () => sources.reduce((m, s) => Math.max(m, s.started), 0),
    [sources]
  );
  const maxWeek = useMemo(
    () => weeks.reduce((m, w) => Math.max(m, w.total), 0),
    [weeks]
  );
  const windowLabel =
    (isMonth && monthLabel) ||
    (WINDOWS.find((w) => w.key === win) || {}).label ||
    "";

  return (
    <div style={{ maxWidth: 820 }}>
      <h2 style={{ marginTop: 0 }}>
        <span role="img" aria-label="compass">🧭</span> Signup Attribution
      </h2>
      <p style={muted}>
        Where new people come from and who brings them. Counts the "How did
        you hear about us?" answer and the <code>?ref=</code> code captured on
        /join and /signup (epic #293). Rows from before that shipped show as
        "not asked", so the attributed share climbs from here.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            type="button"
            onClick={() => setWin(w.key)}
            style={pill(win === w.key)}
          >
            {w.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => load(win)}
          disabled={loading}
          style={{ ...pill(false), background: "#f6f7fa", fontWeight: 500 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {data && (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Stat
              label={`Signups started · ${windowLabel}`}
              value={totals.rows}
              sub="rows with a first-touch date in the window"
            />
            <Stat
              label="Attributed"
              value={pct(totals.attributed, totals.rows)}
              sub={`${totals.attributed} told us a source or used a link`}
            />
            <Stat
              label="Came via a member link"
              value={totals.referred}
              sub={`${referrers.length} referrer${referrers.length === 1 ? "" : "s"}${
                captainCount
                  ? ` · ${totals.referredByCaptains} via ${captainCount} captain${captainCount === 1 ? "" : "s"} ⭐`
                  : ""
              }`}
            />
            <Stat
              label="Attributed → paid"
              value={totals.paidAttributed}
              sub="paying members we can credit to a channel"
            />
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Sources · {windowLabel}
            </div>
            <div style={{ ...muted, marginBottom: 8 }}>
              How far each channel's people get. "Started" is anyone who hit
              the form; the three columns to the right are the funnel.
            </div>
            {sources.length === 0 ? (
              <p style={muted}>No signups in this window.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Source</th>
                      <th style={{ ...th, width: "28%" }}></th>
                      <th style={{ ...th, textAlign: "right" }}>Started</th>
                      <th style={{ ...th, textAlign: "right" }}>Account</th>
                      <th style={{ ...th, textAlign: "right" }}>Approved</th>
                      <th style={{ ...th, textAlign: "right" }}>Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((s) => (
                      <tr
                        key={s.source}
                        style={{
                          color: s.source === "unspecified" ? "#8a93a3" : "#1f2330",
                        }}
                      >
                        <td style={td}>{sourceLabel(s.source)}</td>
                        <td style={td}>
                          {s.source !== "unspecified" && (
                            <Bar value={s.started} max={maxStarted} />
                          )}
                        </td>
                        <td style={tdNum}>{s.started}</td>
                        <td style={tdNum}>{s.accounts}</td>
                        <td style={tdNum}>{s.approved}</td>
                        <td style={{ ...tdNum, fontWeight: 700 }}>{s.paid}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Last 12 weeks · all signups
            </div>
            <div style={{ ...muted, marginBottom: 8 }}>
              Weekly signups started (Mon–Sun), with how many carried a source.
              Independent of the window above.
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Week of</th>
                    <th style={{ ...th, width: "32%" }}></th>
                    <th style={{ ...th, textAlign: "right" }}>Started</th>
                    <th style={{ ...th, textAlign: "right" }}>Attributed</th>
                    <th style={th}>Top sources</th>
                  </tr>
                </thead>
                <tbody>
                  {weeks.map((w) => {
                    const top = Object.entries(w.bySource || {})
                      .filter(([k]) => k !== "unspecified")
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 3)
                      .map(([k, n]) => `${sourceLabel(k)} ${n}`)
                      .join(" · ");
                    return (
                      <tr key={w.weekStart}>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          {fmtWeek(w.weekStart)}
                        </td>
                        <td style={td}>
                          <Bar value={w.total} max={maxWeek} />
                        </td>
                        <td style={tdNum}>{w.total}</td>
                        <td style={tdNum}>
                          {w.total ? `${w.attributed} (${pct(w.attributed, w.total)})` : "–"}
                        </td>
                        <td style={{ ...td, fontSize: 12.5, color: "#5b6b7c" }}>
                          {top || (w.total ? "—" : "")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={card}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                marginBottom: 6,
              }}
            >
              <div style={{ fontWeight: 700, flex: "1 1 auto" }}>
                Referral leaderboard · {windowLabel}
              </div>
              <label
                style={{
                  ...muted,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={captainsOnly}
                  onChange={(e) => setCaptainsOnly(e.target.checked)}
                />
                Captains only{" "}
                <span role="img" aria-label="captain star">⭐</span>
              </label>
            </div>
            <div style={{ ...muted, marginBottom: 8 }}>
              Members whose invite link brought people, ranked by paid, then
              accounts, then total. Tap a row to see who they brought. Codes
              that don't match a member yet are listed as-is.{" "}
              <span role="img" aria-label="star">⭐</span> = on the
              captains roster (Captains tab; link the captain's account or
              email there to get the star).
            </div>
            {isMonth && ranked.length > 0 && (
              <div
                style={{
                  border: `1px solid ${OCEAN}`,
                  background: SEAFOAM,
                  borderRadius: 8,
                  padding: "10px 12px",
                  marginBottom: 10,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  <span role="img" aria-label="trophy">🏆</span> Scoreboard ·{" "}
                  {monthLabel}
                  {captainsOnly ? " · captains" : ""}
                </div>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    fontFamily: "inherit",
                    fontSize: 13.5,
                    margin: "0 0 8px",
                    color: "#1f2330",
                  }}
                >
                  {shoutOut}
                </pre>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await copyText(shoutOut);
                      setCopied(ok ? "Copied — paste it in the chat or on #292." : "Couldn't copy; select the text above.");
                      setTimeout(() => setCopied(""), 4000);
                    }}
                    style={{ ...pill(true), padding: "5px 12px" }}
                  >
                    Copy shout-out
                  </button>
                  {copied && <span style={muted}>{copied}</span>}
                  <span style={{ ...muted, fontSize: 12 }}>
                    What the prize is (#309) is yours to decide; this only names
                    the leader.
                  </span>
                </div>
              </div>
            )}
            {ranked.length === 0 ? (
              <p style={muted}>
                {captainsOnly && referrers.length > 0
                  ? "No captain's link brought anyone in this window. Uncheck \"Captains only\" to see everyone."
                  : "No one has come through a member link in this window yet. The \"Bring a friend\" card on the dashboard is where links come from."}
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {ranked.map((r, i) => {
                  const open = openRef === r.refCode;
                  return (
                    <div
                      key={r.refCode}
                      style={{
                        border: "1px solid #e6e9f0",
                        borderRadius: 8,
                        padding: "8px 10px",
                        background: i === 0 ? "#fbfdf7" : "#fff",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setOpenRef(open ? "" : r.refCode)}
                        style={{
                          all: "unset",
                          display: "flex",
                          width: "100%",
                          alignItems: "center",
                          gap: 10,
                          cursor: "pointer",
                          boxSizing: "border-box",
                        }}
                      >
                        <div style={{ width: 24, fontWeight: 800, color: "#8a93a3" }}>
                          {i + 1}
                        </div>
                        <div style={{ flex: "1 1 160px", minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>
                            {r.resolved
                              ? r.referrerName || r.referrerEmail || "Member"
                              : `Unmatched code ${r.refCode}`}
                            {r.isCaptain && (
                              <span
                                title={`Captain${r.captainName ? `: ${r.captainName}` : ""}`}
                                style={{ marginLeft: 6, fontSize: 12, color: OCEAN }}
                              >
                                <span role="img" aria-label="star">⭐</span> captain
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: "#8a93a3" }}>
                            {r.resolved && r.referrerEmail ? `${r.referrerEmail} · ` : ""}
                            last {fmtDate(r.latestAt)} · code {r.refCode}
                          </div>
                        </div>
                        <div style={{ ...tdNum, padding: 0, fontSize: 13, whiteSpace: "nowrap" }}>
                          <strong>{r.total}</strong> brought · {r.accounts} acct ·{" "}
                          {r.approved} appr · <strong>{r.paid} paid</strong>
                        </div>
                        <div style={{ color: "#8a93a3" }}>{open ? "▾" : "▸"}</div>
                      </button>
                      {open && (
                        <div
                          style={{
                            marginTop: 8,
                            paddingTop: 8,
                            borderTop: "1px dashed #e6e9f0",
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          {r.referred.map((p) => (
                            <div
                              key={p.userId}
                              style={{ fontSize: 13, display: "flex", gap: 8, flexWrap: "wrap" }}
                            >
                              <span style={{ fontWeight: 600 }}>
                                {p.name || p.email || "(no name)"}
                              </span>
                              {p.name && p.email && (
                                <span style={{ color: "#8a93a3" }}>{p.email}</span>
                              )}
                              <span style={{ color: "#5b6b7c" }}>
                                {fmtDate(p.date)} · {STATUS_LABELS[p.status] || p.status}
                                {p.source !== "referral-link" && p.source !== "unspecified"
                                  ? ` · said ${sourceLabel(p.source)}`
                                  : ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Recent attributed signups · {windowLabel}
            </div>
            {recent.length === 0 ? (
              <p style={muted}>Nothing attributed in this window yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>When</th>
                      <th style={th}>Who</th>
                      <th style={th}>Source</th>
                      <th style={th}>Brought by</th>
                      <th style={th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((p) => (
                      <tr key={p.userId}>
                        <td style={{ ...td, whiteSpace: "nowrap", color: "#5b6b7c" }}>
                          {fmtDate(p.date)}
                        </td>
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{p.name || "(no name)"}</div>
                          {p.email && (
                            <div style={{ fontSize: 12, color: "#8a93a3", wordBreak: "break-all" }}>
                              {p.email}
                            </div>
                          )}
                        </td>
                        <td style={td}>{sourceLabel(p.source)}</td>
                        <td style={{ ...td, color: "#5b6b7c" }}>
                          {p.refCode
                            ? p.referrerName || `code ${p.refCode}`
                            : "—"}
                        </td>
                        <td style={td}>{STATUS_LABELS[p.status] || p.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ ...muted, fontSize: 12 }}>
            Generated {new Date(data.generatedAt).toLocaleString()}. First touch
            = earliest date on the row, so legacy rows keep their original
            join date.
          </div>
        </>
      )}
    </div>
  );
};

export default AttributionTab;
